import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useSharedDeck } from '../hooks/useShared'
import { useAuthStore, selectIsAuthenticated } from '../stores/authStore'
import { useLocale } from '../i18n/context'
import type { Card, CardSide } from '../types'
import { findTermSide, isTermSide } from '../utils/cardSides'
import { valueSide } from '../utils/fieldValue'

const IMAGE_LABELS = new Set(['Image', 'Image URL'])
const AUDIO_LABELS = new Set(['Audio', 'Audio URL'])
const IMAGE_URL_RE = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|avif)(\?.*)?$/i
const AUDIO_URL_RE = /^https?:\/\/.+\.(mp3|m4a|ogg|wav|webm|aac|opus|flac)(\?.*)?$/i

function isImageSide(side: CardSide): boolean {
  return (
    side.kind === 'image' ||
    IMAGE_LABELS.has(side.label) ||
    side.content.startsWith('data:image/') ||
    IMAGE_URL_RE.test(side.content.trim())
  )
}

function isAudioSide(side: CardSide): boolean {
  return (
    side.kind === 'audio' ||
    AUDIO_LABELS.has(side.label) ||
    side.content.startsWith('data:audio/') ||
    AUDIO_URL_RE.test(side.content.trim())
  )
}

function renderSideContent(side: CardSide) {
  if (isImageSide(side)) {
    return (
      <img
        src={side.content}
        alt=""
        style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, objectFit: 'contain', display: 'block' }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  if (isAudioSide(side)) {
    return (
      <audio
        src={side.content}
        controls
        preload="metadata"
        style={{ width: '100%', maxWidth: 320 }}
        onClick={(e) => e.stopPropagation()}
      />
    )
  }
  return <span style={{ whiteSpace: 'pre-wrap' }}>{side.content}</span>
}

export default function SharedDeckPage() {
  const { tr } = useLocale()
  const sp = tr.sharedPages
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(selectIsAuthenticated)

  const { data: deck, isLoading, isError } = useSharedDeck(token!)

  if (isLoading) return <SharedPageSkeleton />
  if (isError || !deck) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{sp.deckNotFound}</h2>
          <p style={{ margin: 0, fontSize: 14 }}>{sp.linkExpired}</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Top bar */}
      <div style={{ background: 'var(--bg-header)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>{'CardSmith' /* i18n-exempt: brand name */}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>
            {sp.logIn}
          </button>
          <button onClick={() => navigate('/register')} style={{ padding: '8px 16px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {sp.signUpFree}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>
        {/* Back navigation — only shown to authenticated users */}
        {isAuthenticated && (
          <Link
            to="/explore"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none', marginBottom: 20 }}
          >
            {sp.backToExplore}
          </Link>
        )}

        {/* Deck header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{deck.title}</h1>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{sp.cardCount(deck.card_count)}</span>
            {deck.language && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{deck.language}</span>}
          </div>
          {deck.description && (
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{deck.description}</p>
          )}
        </div>

        {/* Action */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 32 }}>
          <button
            onClick={() => navigate(`/shared/deck/${token}/study`)}
            style={{ padding: '12px 28px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >
            {sp.studyClassic}
          </button>
        </div>

        {/* Sign-up banner (for guests) */}
        {!isAuthenticated && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
            padding: '20px 24px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
            boxShadow: 'var(--shadow-card)',
          }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.trackProgressQ}</p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{sp.trackProgressMsg}</p>
            </div>
            <button onClick={() => navigate('/register')} style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {sp.signUpFreeArrow}
            </button>
          </div>
        )}

        {/* Card list preview */}
        <h2 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
          {sp.cardsHeader(deck.cards.length)}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deck.cards.map((card) => (
            <SharedDeckCardRow key={card.id} card={card} />
          ))}
        </div>
      </div>
    </div>
  )
}

function SharedDeckCardRow({ card }: { card: Card }) {
  const [open, setOpen] = useState(false)

  const term = findTermSide(card.sides)?.content ?? ''
  // ADR-104: the card's definition is its first non-blank Definition.
  const definition = valueSide(card.sides, 'definition')?.content ?? ''
  const extras = card.sides
    .filter((s) => !isTermSide(s) && s.label !== 'Definition')
    .sort((a, b) => a.position - b.position)

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <button
        onClick={() => extras.length > 0 && setOpen((o) => !o)}
        disabled={extras.length === 0}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: extras.length > 0 ? 'pointer' : 'default',
          padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'flex-start', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, fontFamily: 'var(--font-content)', fontWeight: 600, color: 'var(--text-primary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {term}
        </span>
        <div style={{ flex: 2, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontFamily: 'var(--font-content)', color: 'var(--text-secondary)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {definition}
          </span>
          {card.translation && (
            <span style={{ fontSize: 12, fontFamily: 'var(--font-content)', color: 'var(--text-tertiary)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2, fontStyle: 'italic' }}>
              {card.translation}
            </span>
          )}
          {extras.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              {extras.map((s) => (
                <span
                  key={s.id}
                  style={{
                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
                    borderRadius: 4, padding: '2px 6px',
                  }}
                >
                  {s.label}
                </span>
              ))}
            </div>
          )}
        </div>
        {extras.length > 0 && (
          <ChevronDown
            size={16}
            style={{ flexShrink: 0, marginTop: 4, color: 'var(--text-secondary)', opacity: 0.6, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        )}
      </button>

      {open && extras.length > 0 && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--border)' }}>
          {extras.map((s) => (
            <div key={s.id} style={{ paddingTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 4 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 14, fontFamily: 'var(--font-content)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                {renderSideContent(s)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SharedPageSkeleton() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ background: 'var(--bg-header)', height: 56 }} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ height: 36, width: '40%', background: 'var(--border)', borderRadius: 8, marginBottom: 12 }} />
        <div style={{ height: 14, width: '20%', background: 'var(--border)', borderRadius: 4, marginBottom: 28 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ height: 48, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }} />
          ))}
        </div>
      </div>
    </div>
  )
}
